"use client";

import { useEffect, useState } from "react";

/**
 * Subscribes to a CSS media query and returns whether it currently matches.
 *
 * SSR-safe: returns `false` on the server and on the first client render,
 * then updates after mount. To avoid layout flashes / hydration mismatches,
 * prefer Tailwind responsive classes for pure styling and reserve this hook
 * for behavior that genuinely needs JS (e.g. rendering different DOM).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/** Site-wide desktop/mobile split: desktop is `>= 1024px` (Tailwind `lg`). */
export function useIsDesktop(): boolean {
  return useMediaQuery("(min-width: 1024px)");
}

/** Convenience inverse of {@link useIsDesktop}. Mobile/tablet is `< 1024px`. */
export function useIsMobile(): boolean {
  return !useMediaQuery("(min-width: 1024px)");
}
